import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import {
  answerBookingPrompt,
  dismissBookingPrompt,
  fetchBookingPrompts,
} from "../../utils/queries/booking-prompt";
import { Input } from "../Input";
import { Button } from "../Button";
import { getStorage } from "../../utils/storage";
import { onNotification, onNotificationResolved } from "../../utils/socket";
import toastMessage from "../../utils/toastMessage";
import "./styles.css";

// Must match commons/booking/index.js and commons/emergency/index.js on the
// server: the server decides which modal is shown by sending one of these.
//
// This started as the booking prompt and is now the app's one blocking
// surface. An emergency release that turns out to have put an incompatible
// unit into a patient belongs here and nowhere else: a notification in a
// list is read when somebody next looks, and by then the transfusion is
// over.
export const PROMPT_TYPES = {
  STILL_NEEDED: "BOOKING_STILL_NEEDED",
  RECEIVE_PRODUCTS: "BOOKING_RECEIVE_PRODUCTS",
  LAB_UNRESPONDED: "BOOKING_LAB_UNRESPONDED",
  RESCHEDULED: "BOOKING_RESCHEDULED",
  INCOMPATIBLE_UNIT: "EMERGENCY_INCOMPATIBLE_UNIT",
};

const isBookingPrompt = (data) =>
  !!data?.type && Object.values(PROMPT_TYPES).includes(data.type);

// Keyed on what the prompt asks, not on which copy of it arrived. The lab
// reminder is re-sent every five minutes, so by the time anyone looks there
// may be several identical notifications; queued separately they each need
// their own click and the modal looks like it refuses to close.
const keyOf = (prompt) =>
  prompt?.request
    ? `${prompt.type}:${prompt.request}`
    : String(prompt?._id || Math.random());

/**
 * The blocking booking prompt.
 *
 * Deliberately cannot be dismissed: no close button, and a click on the
 * backdrop does nothing. A booking that nobody answers is the thing this
 * exists to prevent, so the only ways out are the buttons.
 *
 * One at a time, oldest first. Two bookings half an hour apart would otherwise
 * stack two unclosable modals on top of each other.
 */
const BookingPrompt = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const [queue, setQueue] = useState([]);
  // The screen a prompt sent them to. Prompts queue one per request, so a
  // laboratory with four unanswered bookings clears one modal and the next
  // lands on top of the request the first one just opened - blocking the
  // very work it asked for, and reading as a modal that refuses to close.
  const [workingOn, setWorkingOn] = useState(null);
  // null -> the question itself; "reason" -> entering why; "confirm" -> the
  // last check before the request is cancelled; "return" -> telling them the
  // units they already hold have to go back.
  const [step, setStep] = useState(null);
  const [reason, setReason] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [followUp, setFollowUp] = useState(null);

  const current = queue[0] || null;

  const push = useCallback((prompt) => {
    if (!isBookingPrompt(prompt)) return;

    setQueue((prev) =>
      prev.some((el) => keyOf(el) === keyOf(prompt)) ? prev : [...prev, prompt]
    );
  }, []);

  // Clears whatever this request was asking, for everyone: the server broadcasts
  // this the moment one person answers, so the rest stop being blocked.
  const dropResolved = useCallback(({ request, types }) => {
    setQueue((prev) =>
      prev.filter(
        (el) =>
          !(
            String(el.request) === String(request) &&
            (!types || types.includes(el.type))
          )
      )
    );
  }, []);

  // Clearing the modal locally is not enough for a prompt nobody answers -
  // a reschedule notice, say. Without telling the server it was read, the
  // next page load would hand it straight back.
  const advance = useCallback((prompt) => {
    if (prompt?._id) dismissBookingPrompt(prompt._id);

    setStep(null);
    setReason("");
    setFollowUp(null);
    setQueue((prev) => prev.slice(1));
  }, []);

  useEffect(() => {
    let cancelled = false;

    const init = async () => {
      const user = await getStorage();

      if (!user?.token) return;
      if (user.account_type === "donor" || user.role === "donor") return;

      const outstanding = await fetchBookingPrompts();

      if (cancelled) return;

      outstanding.forEach(push);

      try {
        await onNotification((data) => {
          if (isBookingPrompt(data)) push(data);
        });
        await onNotificationResolved(dropResolved);
      } catch {
        /* socket unavailable: the fetch above still covers a reload */
      }
    };

    init();

    return () => {
      cancelled = true;
    };
  }, [push, dropResolved]);

  // They are done with that screen the moment they leave it, and whatever
  // else was queued is still outstanding, so it comes back then.
  useEffect(() => {
    if (!workingOn) return;

    let here = location.pathname;

    try {
      here = decodeURIComponent(here);
    } catch {
      /* a path that will not decode is compared as it arrived */
    }

    if (here !== workingOn) setWorkingOn(null);
  }, [location.pathname, workingOn]);

  const requestNo = useMemo(
    () => current?.requestInfo?.requestNo || "",
    [current]
  );

  if (!current || workingOn) return null;

  const submitAnswer = async (answer) => {
    setIsSubmitting(true);

    try {
      const result = await answerBookingPrompt({
        id: current.request,
        answer,
        reason: answer === "no" ? reason : undefined,
      });

      if (result?.alreadyAnswered) {
        toastMessage("success", "This booking was already answered.");
        advance();
        return;
      }

      if (answer === "no") {
        // Only the units the ward already holds have to be walked back, so the
        // server decides this rather than the client guessing from a status.
        if (result?.mustReturnProducts) {
          setStep("return");
          return;
        }

        toastMessage("success", "Request cancelled.");
        advance();
        return;
      }

      if (result?.awaitingReception) {
        setFollowUp({
          shipmentNumber: result.shipmentNumber,
        });
        return;
      }

      advance();
    } catch (error) {
      toastMessage("error", error?.message || "Could not submit your answer");
    } finally {
      setIsSubmitting(false);
    }
  };

  const goToReception = () => {
    const shipmentNumber =
      followUp?.shipmentNumber || current?.requestInfo?.shipmentNumber || "";
    const prompt = current;

    advance(prompt);
    setWorkingOn("/dashboard/reception");

    navigate(
      `/dashboard/reception${
        shipmentNumber
          ? `?shipmentNumber=${encodeURIComponent(shipmentNumber)}`
          : ""
      }`
    );
  };

  // Straight to the request itself, opened on the tab the laboratory needs:
  // sending them to a filtered list still leaves them hunting for the row
  // and then the right tab inside it.
  const goToRequest = () => {
    const no = requestNo;
    const prompt = current;

    advance(prompt);

    if (!no) {
      setWorkingOn("/dashboard/requests");
      navigate("/dashboard/requests");
      return;
    }

    setWorkingOn(`/dashboard/request/${no}`);

    navigate(
      `/dashboard/request/${encodeURIComponent(no)}?tab=${encodeURIComponent(
        "transfer product"
      )}`
    );
  };

  const body = () => {
    // The ward answered yes and the lab had already sent the units.
    if (followUp) {
      return (
        <>
          <p className="booking-prompt-text">
            The laboratory staff has responded to your request
            {requestNo ? ` (${requestNo})` : ""} but you are yet to receive the
            transferred products.
          </p>
          <div className="booking-prompt-actions">
            <Button text="Receive them" onPress={goToReception} />
          </div>
        </>
      );
    }

    if (step === "return") {
      return (
        <>
          <p className="booking-prompt-text">
            You had received the products. Now you should return them to the
            laboratory.
          </p>
          <div className="booking-prompt-actions">
            <Button text="Okay" onPress={() => advance(current)} />
          </div>
        </>
      );
    }

    if (step === "confirm") {
      return (
        <>
          <p className="booking-prompt-text">
            Are you sure you aren&apos;t going to use the booked products?
            This is going to cancel the request automatically.
          </p>
          <div className="booking-prompt-actions">
            <Button
              text="Yes"
              isSubmitting={isSubmitting}
              onPress={() => submitAnswer("no")}
            />
            <Button
              text="No"
              className="btn-default"
              onPress={() => setStep("reason")}
            />
          </div>
        </>
      );
    }

    if (step === "reason") {
      return (
        <>
          <p className="booking-prompt-text">
            Please tell us why the booked products are no longer needed.
          </p>
          <Input
            textarea
            rows={3}
            value={reason}
            placeholder="Enter reason"
            onChange={(e) => setReason(e.target.value)}
          />
          <div className="booking-prompt-actions">
            <Button
              text="Submit"
              isSubmitting={isSubmitting}
              disabled={!reason.trim()}
              onPress={() => setStep("confirm")}
            />
            <Button
              text="Back"
              className="btn-default"
              onPress={() => {
                setReason("");
                setStep(null);
              }}
            />
          </div>
        </>
      );
    }

    if (current.type === PROMPT_TYPES.RECEIVE_PRODUCTS) {
      return (
        <>
          <p className="booking-prompt-text">{current.body}</p>
          <div className="booking-prompt-actions">
            <Button text="Receive them" onPress={goToReception} />
          </div>
        </>
      );
    }

    if (current.type === PROMPT_TYPES.LAB_UNRESPONDED) {
      return (
        <>
          <p className="booking-prompt-text">{current.body}</p>
          <div className="booking-prompt-actions">
            <Button text="Respond to the request" onPress={goToRequest} />
          </div>
        </>
      );
    }

    // Acknowledged rather than answered: the doctor is being told to stop,
    // not asked a question. The button opens the request so they can see
    // which unit it was and what the laboratory found.
    if (current.type === PROMPT_TYPES.INCOMPATIBLE_UNIT) {
      return (
        <>
          <p className="booking-prompt-text">{current.body}</p>
          <div className="booking-prompt-actions">
            <Button text="Open the request" onPress={goToRequest} />
            <Button
              text="I have stopped it"
              className="btn-default"
              onPress={() => advance(current)}
            />
          </div>
        </>
      );
    }

    if (current.type === PROMPT_TYPES.RESCHEDULED) {
      return (
        <>
          <p className="booking-prompt-text">{current.body}</p>
          <div className="booking-prompt-actions">
            <Button text="Okay" onPress={() => advance(current)} />
          </div>
        </>
      );
    }

    return (
      <>
        <p className="booking-prompt-text">
          You have made a booking request{requestNo ? ` (${requestNo})` : ""}.
          Are you still going to use the requested products?
        </p>
        <div className="booking-prompt-actions">
          <Button
            text="Yes"
            isSubmitting={isSubmitting}
            onPress={() => submitAnswer("yes")}
          />
          <Button
            text="No"
            className="btn-default"
            onPress={() => setStep("reason")}
          />
        </div>
      </>
    );
  };

  return (
    <div className="booking-prompt-backdrop" role="dialog" aria-modal="true">
      <div className="booking-prompt-card">
        <h3 className="booking-prompt-title">{current.title || "Booking request"}</h3>
        {body()}
      </div>
    </div>
  );
};

export default BookingPrompt;
